import { Marker, Popup, type MapRef } from "@vis.gl/react-maplibre";
import { useMemo, useRef, useState, type FC } from "react";
import { useRecoilValue } from "recoil";

import { MapLibreStationDot, MapLibreStationPinSvg } from "@ryde/controls";
import {
  MapLibreGeoJsonLine,
  MapLibreMissingStyleImages,
  TRAIL_ARROW_SYMBOL_SPACING_PX,
} from "@ryde/layout";
import {
  getIconMarkerTypeByRoute,
  investigationShowCarAtom,
  investigationShowDetourAtom,
  investigationShowNoGpsAtom,
  investigationShowPlannedAtom,
  investigationShowStationsAtom,
  investigationShowTrackingAtom,
  investigationStationLabelsAtom,
  leafletDashToMapLibreDashArray,
  type DirectionEnum,
  type TLatLngTuple,
} from "@ryde/shared";

import {
  ACTUAL_DASH_ARRAY,
  ACTUAL_LINE_OPACITY,
  ACTUAL_LINE_WEIGHT,
  DETOUR_DASH_ARRAY,
  GAP_DASH_ARRAY,
  GAP_LINE_OPACITY,
  GAP_LINE_WEIGHT,
  MAX_TRAIL_DOTS,
  STATION_COLOR,
  STATION_POPUP_OFFSET_PX,
  TRAIL_ACTUAL_COLOR,
  TRAIL_DETOUR_COLOR,
  TRAIL_NO_GPS_COLOR,
} from "../investigation.constants";
import type { TPlacedPlaybackStation } from "../investigation.models";
import {
  buildTrailDots,
  buildTrailSegments,
  toLineFeature,
} from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlannedRoutePositions } from "../use-planned-route";
import { InvestigationHighlightedStations } from "./investigation.highlighted-stations";
import { InvestigationMapLegend } from "./investigation.legend";
import { InvestigationMapHostStyled } from "./investigation.map.styled";
import { InvestigationPlannedRoute } from "./investigation.planned-route";
import { InvestigationPlaybackMarker } from "./investigation.playback-marker";
import { InvestigationTrailDots } from "./investigation.trail-dots";
import { useInvestigationFollowVehicle } from "./use-investigation-follow-vehicle";
import { useInvestigationMapFit } from "./use-investigation-map-fit";

/**
 * Investigation map on MapLibre: planned route (teal, offset to run parallel) alongside the actual GPS
 * trail (blue), with signal gaps (dotted grey), detours (dashed red), sample dots, station pins and the
 * animated playback vehicle. Every layer is toggled from the top bar.
 */
export const InvestigationMap: FC = () => {
  const { model } = usePlaybackContext();
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [activeStation, setActiveStation] =
    useState<TPlacedPlaybackStation | null>(null);

  const showPlanned = useRecoilValue(investigationShowPlannedAtom);
  const showTracking = useRecoilValue(investigationShowTrackingAtom);
  const showNoGps = useRecoilValue(investigationShowNoGpsAtom);
  const showDetour = useRecoilValue(investigationShowDetourAtom);
  const showStations = useRecoilValue(investigationShowStationsAtom);
  const showCar = useRecoilValue(investigationShowCarAtom);
  const stationLabels = useRecoilValue(investigationStationLabelsAtom);

  const direction = model?.payload.summary.direction as
    DirectionEnum | undefined;

  const stations = useMemo<TPlacedPlaybackStation[]>(
    () =>
      model
        ? model.stations.filter((entry): entry is TPlacedPlaybackStation =>
            Boolean(entry.latLng),
          )
        : [],
    [model],
  );

  // Stored polyline when present, else the OSRM road route (like edit-ride), else a straight connector.
  const plannedPositions = usePlannedRoutePositions(
    model,
  ) as unknown as TLatLngTuple[];

  // Feature built alongside the segment: `<Source>` deep-compares `data` during render, so rebuilding the
  // literal on every station click or layer toggle re-walks every coordinate.
  const segments = useMemo(
    () =>
      buildTrailSegments(model).map(segment => ({
        ...segment,
        feature: toLineFeature(segment.positions as unknown as TLatLngTuple[]),
      })),
    [model],
  );
  const dots = useMemo(() => buildTrailDots(model, MAX_TRAIL_DOTS), [model]);

  const fitPositions = useMemo<TLatLngTuple[]>(
    () => [
      ...plannedPositions,
      ...segments.flatMap(
        segment => segment.positions as unknown as TLatLngTuple[],
      ),
      ...stations.map(entry => entry.latLng),
    ],
    [plannedPositions, segments, stations],
  );

  useInvestigationMapFit({ mapRef, positions: fitPositions, isMapLoaded });
  useInvestigationFollowVehicle({ mapRef, isMapLoaded });

  return (
    <InvestigationMapHostStyled
      mapRef={mapRef}
      isFullscreenEnabled
      onLoadedChange={setIsMapLoaded}
      header={<InvestigationMapLegend />}
    >
      {/* First child on purpose: it installs the runtime-image resolver in a layout effect, which has
          to happen before any sibling symbol layer asks v6 for an icon. */}
      <MapLibreMissingStyleImages />

      {/* Layers wait for load: Marker / Source on a map whose camera is still 0×0 or NaN zoom
          is the same crash as the ride card (`reading '0'` / `'S'`). */}
      {isMapLoaded && showPlanned && (
        <InvestigationPlannedRoute positions={plannedPositions} />
      )}

      {isMapLoaded &&
        showTracking &&
        segments.map((segment, index) => {
          if (segment.kind === "gap") {
            return showNoGps ? (
              <MapLibreGeoJsonLine
                key={`gap-${index}`}
                id={`investigation-gap-${index}`}
                feature={segment.feature}
                color={TRAIL_NO_GPS_COLOR}
                lineWidth={GAP_LINE_WEIGHT}
                opacity={GAP_LINE_OPACITY}
                dashArray={leafletDashToMapLibreDashArray(
                  GAP_DASH_ARRAY,
                  GAP_LINE_WEIGHT,
                )}
              />
            ) : null;
          }

          const isDetour = segment.kind === "detour" && showDetour;

          return (
            <MapLibreGeoJsonLine
              key={`${segment.kind}-${index}`}
              id={`investigation-trail-${index}`}
              feature={segment.feature}
              color={isDetour ? TRAIL_DETOUR_COLOR : TRAIL_ACTUAL_COLOR}
              lineWidth={ACTUAL_LINE_WEIGHT}
              opacity={ACTUAL_LINE_OPACITY}
              dashArray={leafletDashToMapLibreDashArray(
                isDetour ? DETOUR_DASH_ARRAY : ACTUAL_DASH_ARRAY,
                ACTUAL_LINE_WEIGHT,
              )}
              withArrows
              arrowSpacingPx={TRAIL_ARROW_SYMBOL_SPACING_PX}
            />
          );
        })}

      {isMapLoaded && showTracking && <InvestigationTrailDots dots={dots} />}

      {isMapLoaded &&
        showStations &&
        stations.map((entry, index) => {
          const [lat, lng] = entry.latLng;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
          }
          const label = String(entry.station.sequence + 1);

          return (
            <Marker
              key={entry.station.id}
              longitude={lng}
              latitude={lat}
              anchor="center"
              pitchAlignment="viewport"
              rotationAlignment="viewport"
              onClick={clickEvent => {
                clickEvent.originalEvent.stopPropagation();
                setActiveStation(entry);
              }}
            >
              {stationLabels ? (
                <MapLibreStationPinSvg
                  type={getIconMarkerTypeByRoute({
                    isDropoff: Boolean(entry.station.isDropoff),
                    isLastWayPoint: index === stations.length - 1,
                    direction,
                  })}
                  color={STATION_COLOR}
                  label={label}
                />
              ) : (
                <MapLibreStationDot $backgroundColor={STATION_COLOR} />
              )}
            </Marker>
          );
        })}

      {isMapLoaded && <InvestigationHighlightedStations stations={stations} />}

      {isMapLoaded && activeStation && (
        <Popup
          longitude={activeStation.latLng[1]}
          latitude={activeStation.latLng[0]}
          anchor="bottom"
          offset={STATION_POPUP_OFFSET_PX}
          closeOnClick
          onClose={() => setActiveStation(null)}
        >
          {`${activeStation.station.sequence + 1}. ${activeStation.station.name}`}
        </Popup>
      )}

      {isMapLoaded && showCar && <InvestigationPlaybackMarker />}
    </InvestigationMapHostStyled>
  );
};
